// src/firebase/rankings.ts
import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  getDoc,
  Timestamp,
  writeBatch,
} from "firebase/firestore";
import { db } from "./config";
import { withCache, invalidateCache, invalidateCachePrefix } from "../utils/cache";

// ── Cache TTLs ────────────────────────────────────────────────────────────────
//
//  Snapshots are only updated when an admin explicitly triggers
//  recalculateAndSaveSnapshots(), so a 5-minute TTL is very conservative.
//  Athlete results on public profile pages have the same TTL — admin writes
//  automatically bust the per-athlete key so public pages refresh quickly.
//
const TTL_SNAPSHOTS       = 5 * 60 * 1000; // 5 min — published ranking snapshots
const TTL_ATHLETE_RESULTS = 5 * 60 * 1000; // 5 min — public AthleteProfile reads
const TTL_COMPETITIONS    = 5 * 60 * 1000; // 5 min — ranking-competition list
const TTL_SNAPSHOT_KEYS   = 2 * 60 * 1000; // 2 min — admin snapshot enumeration

// ── Types ─────────────────────────────────────────────────────────────────────

export interface RankingCompetition {
  id?: string;
  name: string;
  nameKa: string;
  type: string;
  date: Timestamp;
  year: number;
  month: number;
  location: string;
  totalPoints: number;
  participationPoints: number;
  pointsPerWin: number;
  isActive: boolean;
  createdAt?: Timestamp;
  /**
   * Optional back-reference to the `competitions` collection doc.
   * Set automatically when a competition is created via ManageCompetitions.
   * Allows both sections to stay in sync.
   */
  linkedCompetitionId?: string;
}

export interface RankingResult {
  id?: string;
  competitionId: string;
  competitionName: string;
  competitionType: string;
  athleteId: string;
  athleteFirstName: string;
  athleteLastName: string;
  clubName: string;
  gender: "male" | "female" | "para_male" | "para_female";
  weightClass: string;
  ageGroup: string;
  place: number | null;
  wins: number;
  pointsAwarded: number;
  isOverridden: boolean;
  year: number;
  month: number;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

export interface RankingSnapshotAthlete {
  rank: number;
  athleteId: string;
  firstName: string;
  lastName: string;
  club: string;
  nation: "GEO";
  currentYearPoints: number;
  previousYearPoints: number;
  totalPoints: number;
  imageUrl?: string;
}

export interface RankingSnapshot {
  id?: string;
  gender: string;
  ageGroup: string;
  weightClass: string;
  year: number;
  lastUpdated: Timestamp;
  athletes: RankingSnapshotAthlete[];
}

export interface ClubRankingEntry {
  rank: number;
  clubName: string;
  athleteCount: number;
  totalPoints: number;
}

export interface ClubRankingSnapshot {
  id?: string;
  year: number;
  lastUpdated: Timestamp;
  clubs: ClubRankingEntry[];
}

// ── Ranking Competitions ──────────────────────────────────────────────────────

export async function addRankingCompetition(
  data: Omit<RankingCompetition, "id">
): Promise<string> {
  const ref = await addDoc(collection(db, "rankingCompetitions"), {
    ...data,
    createdAt: Timestamp.now(),
  });
  // Bust the list cache so ManageRankings shows the new entry immediately.
  invalidateCache("rankingCompetitions:all");
  return ref.id;
}

export async function updateRankingCompetition(
  id: string,
  data: Partial<RankingCompetition>
): Promise<void> {
  await updateDoc(doc(db, "rankingCompetitions", id), data);
  invalidateCache("rankingCompetitions:all");
}

export async function deleteRankingCompetition(id: string): Promise<void> {
  await deleteDoc(doc(db, "rankingCompetitions", id));
  invalidateCache("rankingCompetitions:all");
}

/**
 * Returns all ranking competitions ordered by date desc.
 * Cached for TTL_COMPETITIONS (5 min). Automatically invalidated
 * by add / update / delete above.
 */
export async function getRankingCompetitions(): Promise<RankingCompetition[]> {
  return withCache("rankingCompetitions:all", TTL_COMPETITIONS, async () => {
    const q = query(
      collection(db, "rankingCompetitions"),
      orderBy("date", "desc")
    );
    const snap = await getDocs(q);
    return snap.docs.map(
      (d) => ({ id: d.id, ...d.data() } as RankingCompetition)
    );
  });
}

/**
 * Finds a rankingCompetition that is linked to a given competition ID.
 * Used to check if a competition already has a linked ranking entry.
 * Not cached — called once during competition creation (write path).
 */
export async function getRankingCompetitionByLinkedId(
  linkedCompetitionId: string
): Promise<RankingCompetition | null> {
  const q = query(
    collection(db, "rankingCompetitions"),
    where("linkedCompetitionId", "==", linkedCompetitionId)
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() } as RankingCompetition;
}

// ── Ranking Results ───────────────────────────────────────────────────────────

export async function saveRankingResult(
  data: Omit<RankingResult, "id" | "createdAt" | "updatedAt">
): Promise<string> {
  const ref = await addDoc(collection(db, "rankingResults"), {
    ...data,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  });
  // The public AthleteProfile page caches per-athlete results.
  // Bust immediately so the next visit reflects the new result.
  invalidateCache(`athleteResults:${data.athleteId}`);
  return ref.id;
}

export async function updateRankingResult(
  id: string,
  data: Partial<Omit<RankingResult, "id" | "createdAt">>
): Promise<void> {
  await updateDoc(doc(db, "rankingResults", id), {
    ...data,
    updatedAt: Timestamp.now(),
  });
  // If the caller provides athleteId in the partial update, bust that athlete's
  // cache entry. Otherwise the 5-min TTL handles expiry automatically.
  if (data.athleteId) {
    invalidateCache(`athleteResults:${data.athleteId}`);
  }
}

export async function deleteRankingResult(
  id: string,
  athleteId?: string
): Promise<void> {
  await deleteDoc(doc(db, "rankingResults", id));
  // When the caller knows the athleteId (it does on the admin editor),
  // bust that athlete's results cache immediately so the public profile
  // and the editor's next read reflect the deletion. Without it, the
  // 5-min TTL would mask the change.
  if (athleteId) {
    invalidateCache(`athleteResults:${athleteId}`);
  }
}

/**
 * Returns all results for a given competition, sorted by ageGroup → weight → name.
 * NOT cached — this is used in the admin results editor where stale data
 * would be confusing immediately after a save.
 */
export async function getResultsByCompetition(
  competitionId: string
): Promise<RankingResult[]> {
  const q = query(
    collection(db, "rankingResults"),
    where("competitionId", "==", competitionId)
  );
  const snap = await getDocs(q);
  const results = snap.docs.map(
    (d) => ({ id: d.id, ...d.data() } as RankingResult)
  );
  // Sort by ageGroup, then weightClass (numeric order), then athlete name
  return results.sort((a, b) => {
    // 1. Age group order (follows AGE_GROUP_DEFINITIONS order)
    const AGE_ORDER = ["Kids", "Fin", "Cadets", "Juniors", "U21", "Seniors", "Para"];
    const ageA = AGE_ORDER.indexOf(a.ageGroup);
    const ageB = AGE_ORDER.indexOf(b.ageGroup);
    if (ageA !== ageB) return (ageA === -1 ? 999 : ageA) - (ageB === -1 ? 999 : ageB);

    // 2. Gender (male before female)
    if (a.gender !== b.gender) return a.gender.localeCompare(b.gender);

    // 3. Weight class (numeric, "+" suffix last)
    const parseWeight = (w: string) => {
      if (w.startsWith("+")) return 99999;
      return parseInt(w.replace(/[^0-9]/g, ""), 10) || 0;
    };
    if (a.weightClass !== b.weightClass) {
      return parseWeight(a.weightClass) - parseWeight(b.weightClass);
    }

    // 4. Athlete name
    return `${a.athleteLastName} ${a.athleteFirstName}`.localeCompare(
      `${b.athleteLastName} ${b.athleteFirstName}`
    );
  });
}

/**
 * Returns all competition results for a single athlete, sorted by year desc.
 * Used by the public AthleteProfile page.
 * Cached for TTL_ATHLETE_RESULTS (5 min). Bust key `athleteResults:${athleteId}`
 * when a result for that athlete is saved via saveRankingResult().
 */
export async function getResultsByAthlete(
  athleteId: string
): Promise<RankingResult[]> {
  return withCache(`athleteResults:${athleteId}`, TTL_ATHLETE_RESULTS, async () => {
    const q = query(
      collection(db, "rankingResults"),
      where("athleteId", "==", athleteId)
    );
    const snap = await getDocs(q);
    const results = snap.docs.map(
      (d) => ({ id: d.id, ...d.data() } as RankingResult)
    );
    return results.sort((a, b) => {
      if (b.year !== a.year) return b.year - a.year;
      return a.competitionName.localeCompare(b.competitionName);
    });
  });
}

// ── Helper: batch-fetch imageUrl from athletes collection ─────────────────────
// Firestore `in` operator is limited to 30 items per query, so we chunk.
// Only called during recalculateAndSaveSnapshots (write path) — not cached.

async function fetchAthleteImageUrls(
  athleteIds: string[]
): Promise<Record<string, string>> {
  if (athleteIds.length === 0) return {};

  const chunks: string[][] = [];
  for (let i = 0; i < athleteIds.length; i += 30) {
    chunks.push(athleteIds.slice(i, i + 30));
  }

  const imageMap: Record<string, string> = {};
  await Promise.all(
    chunks.map(async (chunk) => {
      const q = query(
        collection(db, "athletes"),
        where("athleteId", "in", chunk)
      );
      const snap = await getDocs(q);
      snap.docs.forEach((d) => {
        const data = d.data() as any;
        if (data.athleteId && data.imageUrl) {
          imageMap[data.athleteId] = data.imageUrl;
        }
      });
    })
  );

  return imageMap;
}

// ── Recalculate & Publish Snapshots ──────────────────────────────────────────

export async function recalculateAndSaveSnapshots(year: number): Promise<void> {
  console.log(`[Rankings] Recalculating for year ${year}...`);

  // 1. Fetch all results for this year
  const q = query(collection(db, "rankingResults"), where("year", "==", year));
  const snap = await getDocs(q);
  const results = snap.docs.map(
    (d) => ({ id: d.id, ...d.data() } as RankingResult)
  );
  console.log(`[Rankings] Found ${results.length} results for ${year}`);

  // 2. Fetch previous-year results for tie-breaking
  const prevYear = year - 1;
  const qPrev = query(
    collection(db, "rankingResults"),
    where("year", "==", prevYear)
  );
  const snapPrev = await getDocs(qPrev);
  const prevResults = snapPrev.docs.map(
    (d) => ({ id: d.id, ...d.data() } as RankingResult)
  );

  // 3. Build previous-year points map
  const prevPointsMap: Record<string, number> = {};
  for (const r of prevResults) {
    const key = `${r.gender}|${r.ageGroup}|${r.weightClass}|${r.athleteId}`;
    prevPointsMap[key] = (prevPointsMap[key] ?? 0) + r.pointsAwarded;
  }

  // 4. Collect all unique athleteIds that appear in this year's results,
  //    then fetch their imageUrls from the athletes collection in one pass.
  const uniqueAthleteIds = [...new Set(results.map((r) => r.athleteId))];
  const imageMap = await fetchAthleteImageUrls(uniqueAthleteIds);
  console.log(
    `[Rankings] Fetched images for ${Object.keys(imageMap).length} athletes`
  );

  // 5. Group current-year results by gender+ageGroup+weightClass
  const groups: Record<string, RankingResult[]> = {};
  for (const r of results) {
    const key = `${r.gender}|${r.ageGroup}|${r.weightClass}`;
    if (!groups[key]) groups[key] = [];
    groups[key].push(r);
  }

  console.log(`[Rankings] ${Object.keys(groups).length} groups to process`);

  const batch = writeBatch(db);
  const now = Timestamp.now();

  // 6. For each group: aggregate, sort, rank, write snapshot
  for (const [groupKey, groupResults] of Object.entries(groups)) {
    const [gender, ageGroup, weightClass] = groupKey.split("|");

    const athleteMap: Record<
      string,
      {
        athleteId: string;
        firstName: string;
        lastName: string;
        club: string;
        currentYearPoints: number;
        previousYearPoints: number;
      }
    > = {};

    for (const r of groupResults) {
      const prevKey = `${r.gender}|${r.ageGroup}|${r.weightClass}|${r.athleteId}`;
      if (!athleteMap[r.athleteId]) {
        athleteMap[r.athleteId] = {
          athleteId: r.athleteId,
          firstName: r.athleteFirstName,
          lastName: r.athleteLastName,
          club: r.clubName || "",
          currentYearPoints: 0,
          previousYearPoints: prevPointsMap[prevKey] ?? 0,
        };
      }
      athleteMap[r.athleteId].currentYearPoints += r.pointsAwarded;
    }

    // Sort: current DESC, then previous DESC
    const sorted = Object.values(athleteMap).sort((a, b) => {
      if (b.currentYearPoints !== a.currentYearPoints)
        return b.currentYearPoints - a.currentYearPoints;
      return b.previousYearPoints - a.previousYearPoints;
    });

    // Assign ranks with tie support
    const athletes: RankingSnapshotAthlete[] = [];
    let currentRank = 1;
    for (let i = 0; i < sorted.length; i++) {
      if (
        i > 0 &&
        (sorted[i].currentYearPoints !== sorted[i - 1].currentYearPoints ||
          sorted[i].previousYearPoints !== sorted[i - 1].previousYearPoints)
      ) {
        currentRank = i + 1;
      }
      const entry: RankingSnapshotAthlete = {
        rank: currentRank,
        athleteId: sorted[i].athleteId,
        firstName: sorted[i].firstName,
        lastName: sorted[i].lastName,
        club: sorted[i].club,
        nation: "GEO",
        currentYearPoints: sorted[i].currentYearPoints,
        previousYearPoints: sorted[i].previousYearPoints,
        totalPoints: sorted[i].currentYearPoints,
      };
      const img = imageMap[sorted[i].athleteId];
      if (img) entry.imageUrl = img;

      athletes.push(entry);
    }

    const snapshotId = `${gender}-${ageGroup}-${weightClass}-${year}`
      .replace(/\s+/g, "_")
      .replace(/[^a-zA-Z0-9_-]/g, "");

    console.log(`[Rankings] Writing snapshot: ${snapshotId} (${athletes.length} athletes)`);

    batch.set(doc(db, "rankingSnapshots", snapshotId), {
      gender,
      ageGroup,
      weightClass,
      year,
      lastUpdated: now,
      athletes,
    });
  }

  // 7. Club rankings
  const clubMap: Record<
    string,
    { athletes: Set<string>; totalPoints: number }
  > = {};
  for (const r of results) {
    const clubKey = r.clubName || "Unknown Club";
    if (!clubMap[clubKey]) {
      clubMap[clubKey] = { athletes: new Set(), totalPoints: 0 };
    }
    clubMap[clubKey].athletes.add(r.athleteId);
    clubMap[clubKey].totalPoints += r.pointsAwarded;
  }

  const sortedClubs = Object.entries(clubMap)
    .map(([clubName, data]) => ({
      clubName,
      athleteCount: data.athletes.size,
      totalPoints: data.totalPoints,
    }))
    .sort((a, b) => b.totalPoints - a.totalPoints);

  let clubRank = 1;
  const clubEntries: ClubRankingEntry[] = sortedClubs.map((c, i) => {
    if (i > 0 && c.totalPoints !== sortedClubs[i - 1].totalPoints) {
      clubRank = i + 1;
    }
    return { rank: clubRank, ...c };
  });

  batch.set(doc(db, "clubRankingSnapshots", `clubs-${year}`), {
    year,
    lastUpdated: now,
    clubs: clubEntries,
  });

  await batch.commit();
  console.log(`[Rankings] Done! Committed batch for year ${year}`);

  // ── Bust all snapshot & club-snapshot caches so public pages serve
  //    fresh data on the very next request after a publish.
  invalidateCachePrefix("rankingSnapshot:");
  invalidateCachePrefix("clubRankingSnapshot:");
  invalidateCachePrefix("snapshotKeys:");
  console.log("[Rankings] Cache invalidated for all snapshot keys.");
}

// ── Read Snapshots (public pages) ─────────────────────────────────────────────

/**
 * Returns the ranking snapshot document for a single gender/ageGroup/weightClass/year
 * combination, or null if it hasn't been published yet.
 *
 * Used by RankingsPage.tsx (via inline withCache) and ClubRankings.tsx.
 * Cached for TTL_SNAPSHOTS (5 min). Automatically invalidated by
 * recalculateAndSaveSnapshots().
 */
export async function getRankingSnapshot(
  gender: string,
  ageGroup: string,
  weightClass: string,
  year: number
): Promise<RankingSnapshot | null> {
  const snapshotId = `${gender}-${ageGroup}-${weightClass}-${year}`
    .replace(/\s+/g, "_")
    .replace(/[^a-zA-Z0-9_-]/g, "");
  const cacheKey = `rankingSnapshot:${snapshotId}`;

  return withCache<RankingSnapshot | null>(cacheKey, TTL_SNAPSHOTS, async () => {
    const ref = doc(db, "rankingSnapshots", snapshotId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() } as RankingSnapshot;
  });
}

/**
 * Returns the club ranking snapshot for a given year, or null if not published.
 * Used by ClubRankings.tsx.
 * Cached for TTL_SNAPSHOTS (5 min). Automatically invalidated by
 * recalculateAndSaveSnapshots().
 */
export async function getClubRankingSnapshot(
  year: number
): Promise<ClubRankingSnapshot | null> {
  return withCache<ClubRankingSnapshot | null>(
    `clubRankingSnapshot:${year}`,
    TTL_SNAPSHOTS,
    async () => {
      const authoritative = await getDocs(query(
        collection(db, "authoritativeRankingSnapshots"),
        where("year", "==", year)
      ));
      if (!authoritative.empty) {
        const byClub = new Map<string, { athleteIds: Set<string>; totalPoints: number }>();
        authoritative.docs.forEach((snapshot) => {
          (snapshot.data().athletes || []).forEach((athlete: any) => {
            const clubName = String(athlete.club || "").trim();
            if (!clubName) return;
            const row = byClub.get(clubName) || { athleteIds: new Set<string>(), totalPoints: 0 };
            row.athleteIds.add(String(athlete.athleteId || ""));
            row.totalPoints += Number(athlete.currentYearPoints || 0);
            byClub.set(clubName, row);
          });
        });
        const clubs = [...byClub.entries()].sort((a, b) => b[1].totalPoints - a[1].totalPoints || a[0].localeCompare(b[0]))
          .map(([clubName, row], index) => ({ rank: index + 1, clubName, athleteCount: row.athleteIds.size, totalPoints: row.totalPoints }));
        return { id: `authoritative-${year}`, year, lastUpdated: Timestamp.now(), clubs };
      }
      const ref = doc(db, "clubRankingSnapshots", `clubs-${year}`);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return { id: snap.id, ...snap.data() } as ClubRankingSnapshot;
    }
  );
}

/**
 * Lists all ranking snapshot document IDs for a given year.
 * Used to enumerate available categories on the admin publish tab.
 * Cached for TTL_SNAPSHOT_KEYS (2 min) — short enough for the admin
 * to see fresh results after a publish without hammering Firestore.
 */
export async function getAllSnapshotKeysForYear(year: number): Promise<string[]> {
  return withCache<string[]>(`snapshotKeys:${year}`, TTL_SNAPSHOT_KEYS, async () => {
    const q = query(
      collection(db, "rankingSnapshots"),
      where("year", "==", year)
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.id);
  });
}
